import { useState, useEffect, useRef } from "react";

// ── Supabase config ──────────────────────────────────────────────────────────
import { SUPABASE_URL, SUPABASE_ANON_KEY as SUPABASE_KEY } from "./supabaseClient";
// ─────────────────────────────────────────────────────────────────────────────

// ── API base URL ──────────────────────────────────────────────────────────
// Same-origin ("") when the API is proxied/hosted alongside the frontend.
// Set REACT_APP_API_BASE_URL to a full origin (e.g. https://api.yourdomain.com)
// when the API runs on a separate dedicated URL, such as on IIS deployments.
const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
// ─────────────────────────────────────────────────────────────────────────────

// ── Supabase client — robust version ─────────────────────────────────────
const sbHeaders = () => ({
  "apikey": SUPABASE_KEY,
  "Authorization": `Bearer ${SUPABASE_KEY}`,
  "Content-Type": "application/json",
  "Prefer": "return=minimal",
});

const supabase = {
  // Load all rows from a table ordered by column 
  load: async (table, orderCol="created_at") => {
    try {
      if (!SUPABASE_URL || !SUPABASE_KEY) return { data: [], error: "No Supabase config" };
      const url = `${SUPABASE_URL}/rest/v1/${table}?select=*&order=${orderCol}.desc&limit=1000`;
      const res = await fetch(url, { headers: sbHeaders() });
      if (!res.ok) { const e = await res.json(); return { data: [], error: e }; }
      const data = await res.json();
      return { data: Array.isArray(data) ? data : [], error: null };
    } catch(e) { return { data: [], error: e.message }; }
  },

  // Upsert rows (insert or update by id)
  upsert: async (table, rows) => {
    try {
      if (!SUPABASE_URL || !SUPABASE_KEY) return { error: "No Supabase config" };
      const arr = Array.isArray(rows) ? rows : [rows];
      if (arr.length === 0) return { error: null };
      const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}`, {
        method: "POST",
        headers: { ...sbHeaders(), "Prefer": "resolution=merge-duplicates,return=minimal" },
        body: JSON.stringify(arr),
      });
      if (!res.ok) { const e = await res.json(); console.error("Supabase upsert error:", e); return { error: e }; }
      return { error: null };
    } catch(e) { console.error("Supabase upsert exception:", e); return { error: e.message }; }
  },

  // Patch a single field on a row
  patch: async (table, id, fields) => {
    try {
      if (!SUPABASE_URL || !SUPABASE_KEY) return { error: "No Supabase config" };
      const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}?id=eq.${encodeURIComponent(id)}`, {
        method: "PATCH",
        headers: sbHeaders(),
        body: JSON.stringify(fields),
      });
      if (!res.ok) { const e = await res.json(); console.error("Supabase patch error:", e); return { error: e }; }
      return { error: null };
    } catch(e) { return { error: e.message }; }
  },

  // Delete a row by id
  delete: async (table, id) => {
    try {
      if (!SUPABASE_URL || !SUPABASE_KEY) return { error: "No Supabase config" };
      const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}?id=eq.${encodeURIComponent(id)}`, {
        method: "DELETE",
        headers: sbHeaders(),
      });
      return { error: res.ok ? null : await res.json() };
    } catch(e) { return { error: e.message }; }
  },
};

const VERTICALS = ["F&B & Hospitality","Retail","Events & Entertainment","Logistics & Warehousing","Beauty & Aesthetics","Banking / Finance","FMCG","Telecommunications","Construction","Other"];
const COUNTRIES = ["MY","SG","TW","HK"];
const COUNTRY_NAMES = { MY:"Malaysia", SG:"Singapore", TW:"Taiwan", HK:"Hong Kong" };
const STAFF_TYPES = ["Gig Workers","Full Time","Both"];

// Dropdown options matching template exactly
const STATUS_OPTS = ["New","Reached Out - Msg","No Pick Up","Busy - Call Back","Not Interested","Interested - Call Back","Interested - Follow Up","Interested - Meet","Quotation","Close Deal","Mismatch Needs","Invalid Number"];
const INDUSTRY_OPTS = ["F&B & Hospitality","FMCG","Logistics & Warehousing","Retail","Events & Entertainment","Telecommunications","Beauty & Aesthetics","Banking / Finance","Construction","Clothing & Apparel","Other"];
const SOURCE_OPTS = ["Rangers Research Tool","Paid - Meta","Paid - LinkedIn","Website - Organic","Referral","Philip Leads","Paid - IG","Paid - FB"];
const STATE_OPTS = ["Selangor","Kuala Lumpur","Pulau Pinang","Johor","Melaka","Putrajaya","Kedah","Kelantan","Negeri Sembilan","Pahang","Perak","Perlis","Terengganu","Labuan","Sabah","Sarawak","Malaysia","Singapore","Taiwan","Hong Kong","Others"];
const ORG_SIZE_OPTS = ["Undefined","1-5","6-10","11-20","21-50"];
const OPP_SIZE_OPTS = ["Micro ( < 1k)","Small (1-5k)","Medium (5-15k)","Large (15 -25k)"];
const DEPT_OPTS = ["Undefined","Operations","HR","Marketing","Sales","R&D","Finance","Engineering","Executive"];
const C = { blue:"#2f2aac", green:"#d4fd49", lavender:"#c2c1fa", bg:"#0a0928", card:"#0e0d2e", inner:"#13124a", border:"#1a1850", white:"#ffffff", grey:"#cecece" };

const scoreColor = s => s>=80?C.green:s>=60?C.lavender:s>=40?"#f9a825":"#888";
const scoreLabel = s => s>=80?"Hot":s>=60?"Warm":s>=40?"Cool":"Weak";
const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2,7)}`;
const today = () => new Date().toISOString().slice(0,10);
const normKey = c => (c.company||"").toLowerCase().replace(/[^a-z0-9]/g,"").trim();

const buildPrompt = (countries, verticals, staffType, maxResults) => {
  const cl = countries.map(c=>COUNTRY_NAMES[c]).join(", ");
  const vl = verticals.join(", ");
  const sd = staffType==="Gig Workers"?"part-time, on-demand, or flexible gig workers":staffType==="Full Time"?"full-time permanent staff":"gig or full-time staff";
  return `You are a B2B lead research assistant for Rangers, a staffing marketplace. Find REAL businesses in ${cl} that need ${sd}. Verticals: ${vl}. Never fabricate.

GOAL: Find as many real matching companies as possible — up to ${maxResults}. Quantity AND quality matter.

SEARCH STRATEGY:
1. First search: broad search for companies matching the query — aim to find 10-15 company names
2. Second search: search for MORE companies if first search returned fewer than ${maxResults} — try different keywords
3. Third search (optional): look up contact details for companies found — website, phone, email, social media
4. Return JSON immediately after 3 searches

CONTACT EXTRACTION: For each company, try to find phone, email, Instagram, Facebook, TikTok from their website. Include the company even if you cannot find contact info — just leave those fields null. Contact info improves the score but is NOT required to include a lead.

Score 0-100:
- countryPresence (25pts): confirmed presence in ${cl}
- multipleOutlets (20pts): multiple locations or large team
- verticalFit (20pts): matches verticals (${vl})
- staffingNeedMatch (15pts): clear need for ${sd}
- contactAvailable (10pts): has at least one contact method
- hiringSignals (10pts): active hiring or growth signals

Return JSON array of up to ${maxResults} leads. Each item:
- name: contact person name or null
- company: string (required)
- country: one of ${countries.map(c=>`"${c}"`).join(",")}
- state: city/area
- industry: industry type (required)
- vertical: closest Rangers vertical (required)
- staffingNeed: "Gig Workers", "Full Time", or "Both"
- orgSize: estimated size or null
- phone: phone number or null
- phone2: second phone or null
- email: email address or null
- instagram: handle or URL or null
- tiktok: handle or URL or null
- facebook: Facebook page URL or null
- website: company website URL or null
- contactPage: contact page URL or null
- temporaryManpowerNeeds: staffing needs description or null
- qualifyingScore: 0-100 integer
- scoreBreakdown: {countryPresence, multipleOutlets, verticalFit, staffingNeedMatch, contactAvailable, hiringSignals}
- fitReason: one sentence

CRITICAL: You MUST aim to return exactly ${maxResults} results. If your first search finds fewer than ${maxResults} companies, do ANOTHER search with different keywords — synonyms, add city names, search by subsector. After 4 searches return whatever you have found. Never return fewer than ${maxResults} without trying multiple search angles.

Respond ONLY with valid JSON array. No markdown, no preamble.`;
};

// ── Score ring ────────────────────────────────────────────────────────────────
const ScoreRing = ({score}) => {
  const color = scoreColor(score), r=20, circ=2*Math.PI*r, dash=(score/100)*circ;
  return (
    <div style={{position:"relative",width:52,height:52,flexShrink:0}}>
      <svg width="52" height="52" style={{transform:"rotate(-90deg)"}}>
        <circle cx="26" cy="26" r={r} fill="none" stroke={C.border} strokeWidth="3"/>
        <circle cx="26" cy="26" r={r} fill="none" stroke={color} strokeWidth="3" strokeDasharray={`${dash} ${circ-dash}`} strokeLinecap="round"/>
      </svg>
      <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
        <span style={{fontSize:"13px",fontWeight:900,color}}>{score}</span>
      </div>
    </div>
  );
};

// ── Score breakdown bars ──────────────────────────────────────────────────────
const ScoreBreakdown = ({breakdown, score}) => {
  const color = scoreColor(score);
  const bars = [
    {label:"Country Presence",key:"countryPresence",max:25},
    {label:"Multiple Outlets",key:"multipleOutlets",max:20},
    {label:"Vertical Fit",key:"verticalFit",max:20},
    {label:"Staffing Need Match",key:"staffingNeedMatch",max:15},
    {label:"Contact Available",key:"contactAvailable",max:10},
    {label:"Hiring Signals",key:"hiringSignals",max:10},
  ];
  return (
    <div style={{background:C.inner,borderRadius:"8px",padding:"12px 14px"}}>
      <div style={{fontSize:"10px",color:C.grey,fontWeight:700,letterSpacing:"0.06em",marginBottom:"10px"}}>SCORE BREAKDOWN · {score}/100</div>
      {bars.map(({label,key,max}) => {
        const val = breakdown?.[key]||0;
        return (
          <div key={key} style={{marginBottom:"7px"}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:"3px"}}>
              <span style={{fontSize:"10px",color:C.grey}}>{label}</span>
              <span style={{fontSize:"10px",color,fontWeight:700}}>{val}/{max}</span>
            </div>
            <div style={{background:C.border,borderRadius:"3px",height:"5px",overflow:"hidden"}}>
              <div style={{width:`${(val/max)*100}%`,background:color,height:"100%",borderRadius:"3px",transition:"width 0.4s"}}/>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Contact block ─────────────────────────────────────────────────────────────
const ContactBlock = ({lead}) => {
  const rows = [
    {icon:"📧",val:lead.email,href:`mailto:${lead.email}`,color:C.green},
    {icon:"📞",val:lead.phone,href:null,color:C.white},
    {icon:"🌐",val:lead.website?lead.website.replace(/^https?:\/\//,"").split("/")[0]:null,href:lead.website,color:C.lavender},
    {icon:"📸",val:lead.instagram,href:null,color:C.white},
    {icon:"💼",val:lead.linkedIn?"LinkedIn":null,href:lead.linkedIn,color:C.lavender},
    {icon:"🔗",val:lead.contactPage?"Contact Page":null,href:lead.contactPage,color:C.lavender},
  ].filter(r=>r.val);
  return (
    <div style={{background:C.inner,borderRadius:"8px",padding:"10px 12px"}}>
      <div style={{fontSize:"10px",color:C.grey,fontWeight:700,letterSpacing:"0.06em",marginBottom:"8px"}}>CONTACT INFO</div>
      {rows.length===0 && <div style={{fontSize:"11px",color:"#555"}}>No public contact info found</div>}
      {rows.map((r,i) => (
        <div key={i} style={{display:"flex",alignItems:"center",gap:"8px",padding:"3px 0",borderBottom:`1px solid ${C.border}`}}>
          <span style={{fontSize:"12px",width:"18px"}}>{r.icon}</span>
          {r.href ? <a href={r.href} target="_blank" rel="noreferrer" style={{color:r.color,fontSize:"11px",fontWeight:600,textDecoration:"none",wordBreak:"break-all"}}>{r.val}</a>
            : <span style={{color:r.color,fontSize:"11px",wordBreak:"break-all"}}>{r.val}</span>}
        </div>
      ))}
    </div>
  );
};

// ── Lead card (search + history) ──────────────────────────────────────────────
const LeadCard = ({lead,mode,isExpanded,onToggle,isSelected,onSelect,onAccept,onReject,showCheckbox}) => {
  const color = scoreColor(lead.qualifyingScore);
  const hasContact = !!(lead.phone||lead.email);
  return (
    <div style={{background:C.card,border:`1px solid ${isExpanded?color+"66":isSelected?C.blue:C.border}`,borderRadius:"10px",overflow:"hidden",transition:"border-color 0.15s",opacity:lead.historyStatus==="Rejected"?0.45:1}}>
      <div style={{padding:"12px 16px",display:"flex",alignItems:"center",gap:"10px"}}>
        {showCheckbox && <input type="checkbox" checked={isSelected} onChange={()=>onSelect(lead.id)} style={{width:"16px",height:"16px",accentColor:C.green,flexShrink:0,cursor:"pointer"}}/>}
        <div onClick={onToggle} style={{display:"flex",alignItems:"center",gap:"10px",flex:1,cursor:"pointer"}}>
          <ScoreRing score={lead.qualifyingScore}/>
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:"flex",alignItems:"center",gap:"6px",flexWrap:"wrap",marginBottom:"3px"}}>
              <span style={{fontWeight:800,fontSize:"14px",color:C.white}}>{lead.company}</span>
              {lead.name && <span style={{fontSize:"11px",color:C.grey}}>· {lead.name}</span>}
              <span style={{background:color+"18",color,border:`1px solid ${color}44`,fontSize:"10px",fontWeight:800,padding:"2px 7px",borderRadius:"9px"}}>{scoreLabel(lead.qualifyingScore)}</span>
              <span style={{background:C.border,color:C.lavender,fontSize:"10px",padding:"2px 7px",borderRadius:"9px"}}>{lead.vertical||lead.industry}</span>
              {lead.staffingNeed && <span style={{background:"#1a2e1a",color:C.green,fontSize:"10px",padding:"2px 7px",borderRadius:"9px"}}>{lead.staffingNeed}</span>}
              {hasContact && <span style={{fontSize:"10px",color:C.green,fontWeight:700}}>📧</span>}
              {lead.historyStatus && <span style={{background:lead.historyStatus==="Accepted"?"#1a2e1a":"#2e1a1a",color:lead.historyStatus==="Accepted"?C.green:"#ff7070",fontSize:"10px",fontWeight:700,padding:"2px 7px",borderRadius:"9px"}}>{lead.historyStatus}</span>}
            </div>
            <div style={{fontSize:"11px",color:"#888"}}>📍 {lead.state}, {COUNTRY_NAMES[lead.country]||lead.country}{lead.orgSize?` · 👥 ${lead.orgSize}`:""}</div>
          </div>
          <span style={{color:"#444",fontSize:"12px",flexShrink:0}}>{isExpanded?"▲":"▼"}</span>
        </div>
      </div>
      {isExpanded && (
        <div style={{borderTop:`1px solid ${C.border}`,padding:"14px 16px"}}>
          {lead.fitReason && <div style={{fontSize:"12px",color:C.grey,fontStyle:"italic",marginBottom:"12px"}}>💡 {lead.fitReason}</div>}
          {lead.temporaryManpowerNeeds && (
            <div style={{fontSize:"12px",color:C.white,marginBottom:"12px",background:C.inner,padding:"8px 12px",borderRadius:"7px"}}>
              <span style={{fontSize:"10px",color:C.grey,fontWeight:700,display:"block",marginBottom:"4px"}}>MANPOWER NEEDS</span>
              {lead.temporaryManpowerNeeds}
            </div>
          )}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"12px"}}>
            <ContactBlock lead={lead}/>
            <ScoreBreakdown breakdown={lead.scoreBreakdown} score={lead.qualifyingScore}/>
          </div>
          {mode==="history" && (
            <div style={{display:"flex",gap:"8px",marginTop:"12px",justifyContent:"flex-end"}}>
              {lead.historyStatus!=="Accepted" && <button onClick={()=>onAccept(lead.id)} style={{background:C.green,color:C.bg,border:"none",borderRadius:"6px",fontFamily:"'Mulish',sans-serif",fontWeight:800,fontSize:"12px",padding:"7px 16px",cursor:"pointer"}}>✓ Add to Pipeline</button>}
              {lead.historyStatus!=="Rejected" && <button onClick={()=>onReject(lead.id)} style={{background:"none",border:"1px solid #7a1c1c",borderRadius:"6px",color:"#ff7070",fontFamily:"'Mulish',sans-serif",fontSize:"12px",padding:"7px 16px",cursor:"pointer"}}>✗ Reject</button>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main app ──────────────────────────────────────────────────────────────────
export default function App({ session, onSignOut }) {
  const [query,setQuery] = useState("");
  const [selVerticals,setSelVerticals] = useState([...VERTICALS]);
  const [selCountries,setSelCountries] = useState(["MY"]);
  const [staffType,setStaffType] = useState("Gig Workers");
  const [maxResults,setMaxResults] = useState(10);
  const [minScoreFilter,setMinScoreFilter] = useState(0);
  const [searchResults,setSearchResults] = useState({});
  const [history,setHistory] = useState([]);
  const [pipeline,setPipeline] = useState([]);
  const [selectedIds,setSelectedIds] = useState(new Set());
  const [loading,setLoading] = useState(false);
  const [error,setError] = useState(null);
  const [activeTab,setActiveTab] = useState("search");
  const [expandedId,setExpandedId] = useState(null);
  const [searchCount,setSearchCount] = useState(0);
  const [lastBatch,setLastBatch] = useState(null);
  const [pipelineMinScore,setPipelineMinScore] = useState(0);
  const [pipelineSortBy,setPipelineSortBy] = useState("qualifyingScore");
  const [historyFilter,setHistoryFilter] = useState("All");
  const inputRef = useRef(null);

  // ── Load data from Supabase on mount ─────────────────────────────────────
  const [dbLoading, setDbLoading] = useState(true);
  useEffect(() => {
    const loadData = async () => {
      try {
        if (!SUPABASE_URL || !SUPABASE_KEY) { console.warn("Supabase not configured"); setDbLoading(false); return; }
        // Load pipeline
        const { data: pData, error: pErr } = await supabase.load("pipeline_leads", "created_at");
        if (pErr) { console.error("Pipeline load error:", pErr); }
        else { console.log("Pipeline loaded:", pData?.length, "rows"); }
        if (pData && Array.isArray(pData)) {
          setPipeline(pData.map(r => ({
            id: r.id, company: r.company, name: r.name,
            phone: r.phone, phone2: r.phone2,
            email: r.email, instagram: r.instagram,
            tiktok: r.tiktok, facebook: r.facebook,
            website: r.website, state: r.state, country: r.country,
            industry: r.industry, vertical: r.vertical, orgSize: r.org_size,
            staffingNeed: r.staffing_need, qualifyingScore: r.qualifying_score,
            scoreBreakdown: r.score_breakdown, fitReason: r.fit_reason,
            temporaryManpowerNeeds: r.temporary_manpower_needs,
            source: r.source, pipelineStatus: r.pipeline_status,
            department: r.department, oppSize: r.opp_size, notes: r.notes,
            dateCreated: r.date_created, dateFirstContact: r.date_first_contact,
            dateLastContact: r.date_last_contact,
          })));
        }
        // Load history
        const { data: hData, error: hErr } = await supabase.load("search_history", "created_at");
        if (hErr) console.error("History load error:", hErr);
        if (hData && Array.isArray(hData)) {
          setHistory(hData.map(r => ({
            id: r.id, company: r.company, name: r.name,
            phone: r.phone, phone2: r.phone2,
            email: r.email, instagram: r.instagram,
            tiktok: r.tiktok, facebook: r.facebook,
            website: r.website, state: r.state, country: r.country,
            industry: r.industry, vertical: r.vertical, orgSize: r.org_size,
            staffingNeed: r.staffing_need, qualifyingScore: r.qualifying_score,
            scoreBreakdown: r.score_breakdown, fitReason: r.fit_reason,
            temporaryManpowerNeeds: r.temporary_manpower_needs,
            historyStatus: r.history_status, source: r.source,
            dateCreated: r.date_created,
          })));
        }
      } catch(e) { console.error("Supabase load error:", e); }
      finally { setDbLoading(false); }
    };
    loadData();
  }, []);

  const toggleVertical = v => setSelVerticals(p=>p.includes(v)?p.filter(x=>x!==v):[...p,v]);
  const toggleCountry = c => setSelCountries(p=>p.includes(c)?p.filter(x=>x!==c):[...p,c]);
  const isDupe = lead => history.some(h=>normKey(h)===normKey(lead));

  // ── Search ────────────────────────────────────────────────────────────────
  const searchLeads = async () => {
    if (!query.trim()||selCountries.length===0||selVerticals.length===0) return;
    setLoading(true); setError(null); setSearchResults({}); setSelectedIds(new Set()); setLastBatch(null);
    const cap = Math.min(maxResults, 12);
    const vf = selVerticals.length<VERTICALS.length?` Verticals: ${selVerticals.slice(0,4).join(", ")}.`:"";
    const countryCtx = selCountries.map(c=>COUNTRY_NAMES[c]).join(" and ");
    const queryHasLocation = selCountries.some(c=>query.toLowerCase().includes(COUNTRY_NAMES[c].toLowerCase())||query.toLowerCase().includes(c.toLowerCase()));
    const searchQuery = queryHasLocation ? query : `${query} in ${countryCtx}`;
    const userMsg = `Find exactly ${cap} real businesses for: "${searchQuery}".${vf}

INSTRUCTIONS:
1. Search broadly — try multiple keyword variations to find enough companies
2. Keep searching until you have AT LEAST ${cap} companies — do up to 4 searches if needed
3. If first search returns fewer than ${cap} results, immediately do another search with different keywords (e.g. add city names, try synonyms like "event organizer", "event agency", "MICE company")
4. For each company try to find phone, email, Instagram, Facebook from their website
5. Include companies even without contact info

YOU MUST AIM TO RETURN EXACTLY ${cap} RESULTS. Do not return fewer than ${cap} unless you have genuinely exhausted all search options after 4 searches.`;
    try {
      const controller = new AbortController();
      const timeout = setTimeout(()=>controller.abort(), 95000);
      const res = await fetch(`${API_BASE}/api/search-leads`,{
        method:"POST",
        signal:controller.signal,
        headers:{
          "Content-Type":"application/json",
          "Authorization":`Bearer ${session.access_token}`,
        },
        body:JSON.stringify({
          system:buildPrompt(selCountries,selVerticals,staffType,cap),
          userMsg,
        }),
      });
      clearTimeout(timeout);
      const data = await res.json();
      if (data.error){setError(`API error: ${data.error.message}`);setLoading(false);return;}
      const text = data.content?.filter(b=>b.type==="text").map(b=>b.text).join("")||"";

      // Robust JSON extraction - find the leads array
      let parsed = null;
      // Try to find a JSON array containing company objects
      const arrayMatches = [];
      let depth = 0, start = -1;
      for (let i = 0; i < text.length; i++) {
        if (text[i] === "[") { if (depth === 0) start = i; depth++; }
        else if (text[i] === "]") { depth--; if (depth === 0 && start !== -1) { arrayMatches.push(text.slice(start, i+1)); start = -1; } }
      }
      // Find the array that has company objects
      for (const m of arrayMatches.reverse()) {
        if (m.includes('"company"') || m.includes('"qualifyingScore"')) {
          try {
            // Clean email protection artifacts before parsing
            const cleaned = m
              .replace(/\[email[^\]]*protected\]/gi, "")
              .replace(/\[email protected\]/gi, "");
            parsed = JSON.parse(cleaned);
            break;
          } catch(e) { continue; }
        }
      }
      if (!parsed || !Array.isArray(parsed) || parsed.length === 0) {
        setError("No results found. Try a different search query.");
        setLoading(false); return;
      }

      // Filter duplicates and tag leads
      const clean = parsed.filter(l => !isDupe(l));
      const tagged = clean.map(l=>({
        ...l,
        id:uid(),
        dateCreated:today(),
        status:"New",
        source:"Rangers Research Tool",
        industry: l.industry || l.vertical || "",
        vertical: l.vertical || l.industry || "",
      }));
      const grouped = {};
      selCountries.forEach(c=>{grouped[c]=[];});
      tagged.forEach(l=>{const c=l.country||selCountries[0];if(!grouped[c])grouped[c]=[];grouped[c].push(l);});
      Object.keys(grouped).forEach(c=>grouped[c].sort((a,b)=>b.qualifyingScore-a.qualifyingScore));
      const totalClean = Object.values(grouped).flat().length;
      const dupesRemoved = parsed.length - totalClean;
      setSearchResults(grouped);
      setSearchCount(n=>n+1);
      if (dupesRemoved > 0) setLastBatch(prev=>({...prev, dupesRemoved}));
    } catch(err) {
      if (err.name==="AbortError") { setError("Search timed out. Try fewer results or a simpler query."); }
      else { setError(`Search failed: ${err.message}`); }
    } finally { setLoading(false); }
  };

    const allVisibleIds = Object.values(searchResults).flat().filter(l=>l.qualifyingScore>=minScoreFilter).map(l=>l.id);
  const toggleSelect = id => setSelectedIds(p=>{const n=new Set(p);n.has(id)?n.delete(id):n.add(id);return n;});

  // ── Add to pipeline ───────────────────────────────────────────────────────
  const addSelectedToPipeline = async () => {
    const all = Object.values(searchResults).flat();
    const toAdd = all.filter(l=>selectedIds.has(l.id));
    const newH = toAdd.map(l=>({...l,historyStatus:"Accepted"}));
    setHistory(p=>[...p,...newH]);
    setPipeline(p=>[...p,...toAdd].sort((a,b)=>b.qualifyingScore-a.qualifyingScore));
    setLastBatch({added:toAdd.length});
    setSelectedIds(new Set()); setSearchResults({}); setActiveTab("pipeline");
    // Save to Supabase
    const dbRows = toAdd.map(l=>({
      id: l.id, company: l.company, name: l.name||null,
      phone: l.phone||null, phone2: l.phone2||null,
      email: l.email||null, instagram: l.instagram||null,
      tiktok: l.tiktok||null, facebook: l.facebook||null,
      website: l.website||null, state: l.state||null, country: l.country||null,
      industry: l.industry||l.vertical||null, vertical: l.vertical||null,
      org_size: l.orgSize||null, staffing_need: l.staffingNeed||null,
      qualifying_score: l.qualifyingScore||0, score_breakdown: l.scoreBreakdown||null,
      fit_reason: l.fitReason||null, temporary_manpower_needs: l.temporaryManpowerNeeds||null,
      source: l.source||"Rangers Research Tool", pipeline_status: "New",
      date_created: l.dateCreated||today(),
    }));
    const { error: pipeErr } = await supabase.upsert("pipeline_leads", dbRows);
    if (pipeErr) console.error("Pipeline save error:", pipeErr);
    else console.log("Pipeline saved to Supabase:", dbRows.length, "rows");
    // Also save to history
    const histRows = toAdd.map(l=>({
      id: l.id+"_h", company: l.company, name: l.name||null, phone: l.phone||null,
      email: l.email||null, instagram: l.instagram||null, website: l.website||null,
      state: l.state||null, country: l.country||null, industry: l.industry||null,
      vertical: l.vertical||null, org_size: l.orgSize||null,
      staffing_need: l.staffingNeed||null, qualifying_score: l.qualifyingScore||0,
      score_breakdown: l.scoreBreakdown||null, fit_reason: l.fitReason||null,
      temporary_manpower_needs: l.temporaryManpowerNeeds||null,
      history_status: "Accepted", source: l.source||"Rangers Research Tool",
      date_created: today(),
    }));
    const { error: histErr } = await supabase.upsert("search_history", histRows);
    if (histErr) console.error("History save error:", histErr);
    else console.log("History saved to Supabase:", histRows.length, "rows");
  };

  // ── History actions ───────────────────────────────────────────────────────
  const acceptFromHistory = async (id) => {
    setHistory(p=>p.map(l=>l.id===id?{...l,historyStatus:"Accepted"}:l));
    const lead = history.find(l=>l.id===id);
    if (lead&&!pipeline.find(p=>p.id===id)) {
      const newLead = {...lead, dateCreated:lead.dateCreated||today()};
      setPipeline(p=>[...p,newLead].sort((a,b)=>b.qualifyingScore-a.qualifyingScore));
      await supabase.upsert("pipeline_leads", [{
        id: lead.id, company: lead.company, name: lead.name||null,
        phone: lead.phone||null, email: lead.email||null,
        instagram: lead.instagram||null, website: lead.website||null,
        state: lead.state||null, country: lead.country||null,
        industry: lead.industry||null, vertical: lead.vertical||null,
        org_size: lead.orgSize||null, staffing_need: lead.staffingNeed||null,
        qualifying_score: lead.qualifyingScore||0, score_breakdown: lead.scoreBreakdown||null,
        fit_reason: lead.fitReason||null, source: lead.source||"Rangers Research Tool",
        pipeline_status: "New", date_created: lead.dateCreated||today(),
      }]);
    }
    await supabase.patch("search_history", id+"_h", { history_status: "Accepted" });
  };
  const rejectFromHistory = async (id) => {
    setHistory(p=>p.map(l=>l.id===id?{...l,historyStatus:"Rejected"}:l));
    setPipeline(p=>p.filter(l=>l.id!==id));
    await supabase.delete("pipeline_leads", id);
    await supabase.patch("search_history", id+"_h", { history_status: "Rejected" });
  };

  // ── Update pipeline lead field + save to Supabase ────────────────────────
  const updateLead = async (id, field, value) => {
    setPipeline(p=>p.map(l=>l.id===id?{...l,[field]:value}:l));
    // Map JS field name to DB column name
    const fieldMap = {
      company:"company", name:"name", phone:"phone", phone2:"phone2",
      email:"email", instagram:"instagram", tiktok:"tiktok",
      facebook:"facebook", website:"website", state:"state",
      country:"country", industry:"industry", orgSize:"org_size",
      staffingNeed:"staffing_need", temporaryManpowerNeeds:"temporary_manpower_needs",
      source:"source", pipelineStatus:"pipeline_status", department:"department",
      oppSize:"opp_size", notes:"notes", dateFirstContact:"date_first_contact",
      dateLastContact:"date_last_contact", dateCreated:"date_created",
    };
    const dbField = fieldMap[field];
    if (dbField) {
      await supabase.patch("pipeline_leads", id, { [dbField]: value });
    }
  };

  // ── Export CSV (matches template column order) ───────────────────────────
  const exportXLSX = () => {
    try {
      // Column order matches Rangers Lead Template exactly
      const headers = [
        "Name PIC","Company","General Line","Handphone Number","Email",
        "Instagram Account Handle","Facebook Account Handle","Qualifying Score",
        "State","Country","Industry","Org Size",
        "Date First Contact","Date Last Contact","Date Created",
        "Source","Status","Department","Opp Size",
        "Temporary Manpower Needs","Notes","Website","Staffing Need","Fit Reason"
      ];
      const safe = (v) => {
        if (v === null || v === undefined) return "";
        const s = String(v).replace(/"/g, '""');
        return `"${s}"`;
      };
      const lines = [
        headers.map(h => safe(h)).join(","),
        ...pipelineLeads.map(l => [
          safe(l.company),
          safe(l.website),
          safe(l.qualifyingScore),
          safe(l.temporaryManpowerNeeds),
          safe(l.name),
          safe(l.phone),
          safe(l.phone2),
          safe(l.email),
          safe(l.instagram),
          safe(l.tiktok),
          safe(l.facebook),
          safe(l.state),
          safe(l.industry||l.vertical),
          safe(l.orgSize),
          safe(l.dateCreated),
          safe(l.dateFirstContact),
          safe(l.dateLastContact),
          safe(l.source||"Rangers Research Tool"),
          safe(l.pipelineStatus||"New"),
          safe(l.department),
          safe(l.oppSize),
          safe(l.notes),
        ].join(","))
      ];
      const csv = "\uFEFF" + lines.join("\r\n");
      const blob = new Blob([csv], {type:"text/csv;charset=utf-8;"});
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "rangers-leads-" + today() + ".csv";
      document.body.appendChild(a);
      a.click();
      setTimeout(()=>{ URL.revokeObjectURL(url); document.body.removeChild(a); }, 2000);
    } catch(err) {
      alert("Export failed: " + err.message);
    }
  };

  // ── Sync to Google Sheet ──────────────────────────────────────────────────
  const SHEET_ID = "1ifPKtQmEvGrZVWvuqGo3elk9RgqCEp99ezBV4_TiREw";
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);

  const syncToGoogleSheet = async () => {
    setSyncing(true); setSyncMsg(null);
    try {
      const res = await fetch(`${API_BASE}/api/sync-sheet`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          sheetId: SHEET_ID,
          leads: pipelineLeads.map(l => ([
            // A: Company
            l.company||"",
            // B: Website
            l.website||"",
            // C: Score
            String(l.qualifyingScore||""),
            // D: Manpower Needs
            l.temporaryManpowerNeeds||"",
            // E: Name PIC
            l.name||"",
            // F: Phone
            l.phone||"",
            // G: Phone 2
            l.phone2||"",
            // H: Email
            l.email||"",
            // I: Instagram
            l.instagram||"",
            // J: TikTok
            l.tiktok||"",
            // K: Facebook
            l.facebook||"",
            // L: State
            l.state||"",
            // M: Industry
            l.industry||l.vertical||"",
            // N: Org Size
            l.orgSize||"",
            // O: Date Created
            l.dateCreated||today(),
            // P: Date First Contact
            l.dateFirstContact||"",
            // Q: Date Last Contact
            l.dateLastContact||"",
            // R: Source
            l.source||"Rangers Research Tool",
            // S: Status
            l.pipelineStatus||"New",
            // T: Department
            l.department||"",
            // U: Opp Size
            l.oppSize||"",
            // V: Notes
            l.notes||"",
          ]))
        }),
      });
      const data = await res.json();
      if (data.error) { setSyncMsg({type:"error", text: data.error}); }
      else { setSyncMsg({type:"success", text: `✓ ${data.added} new leads synced to Google Sheet. ${data.skipped} duplicates skipped.`}); }
    } catch(err) {
      setSyncMsg({type:"error", text:"Sync failed: " + err.message});
    } finally { setSyncing(false); }
  };

  // ── Filtered pipeline ─────────────────────────────────────────────────────
  const pipelineLeads = pipeline
    .filter(l=>l.qualifyingScore>=pipelineMinScore)
    .sort((a,b)=>pipelineSortBy==="name"?a.company.localeCompare(b.company):b.qualifyingScore-a.qualifyingScore);

  const filteredHistory = history.filter(l=>historyFilter==="All"||(l.historyStatus||"Pending")===historyFilter);
  const tierCounts = {Hot:0,Warm:0,Cool:0,Weak:0};
  pipeline.forEach(l=>tierCounts[scoreLabel(l.qualifyingScore)]++);

  const QUICK = ["F&B chains 5+ outlets","event management companies","logistics warehousing","retail fashion brands","corporate offices admin","catering companies","supermarket chains","marketing agencies"];

  const btnStyle = (active) => ({cursor:"pointer",borderRadius:"6px",fontFamily:"'Mulish',sans-serif",fontWeight:700,fontSize:"11px",padding:"5px 12px",border:"1px solid",background:active?C.blue:C.inner,color:active?C.green:C.grey,borderColor:active?C.blue:"#2a2870",transition:"all 0.12s"});

  return (
    <div style={{fontFamily:"'Mulish',sans-serif",background:C.bg,minHeight:"100vh",color:C.white,width:"100%",boxSizing:"border-box"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Mulish:wght@400;600;700;900&display=swap');
        *{box-sizing:border-box;}
        ::-webkit-scrollbar{width:4px;} ::-webkit-scrollbar-track{background:${C.card};} ::-webkit-scrollbar-thumb{background:${C.blue};border-radius:2px;}
        .fade-in{animation:fi 0.2s ease;} @keyframes fi{from{opacity:0;transform:translateY(5px);}to{opacity:1;transform:translateY(0);}}
        .pulse{animation:pl 1.4s ease-in-out infinite;} @keyframes pl{0%,100%{opacity:1;}50%{opacity:0.3;}}
        input:focus,select:focus{outline:none;border-color:${C.green}!important;}
        .chip{cursor:pointer;transition:all 0.12s;user-select:none;} .chip:hover{border-color:${C.green}!important;color:${C.green}!important;}
        .tab{cursor:pointer;border:none;background:none;font-family:'Mulish',sans-serif;}
        input[type=range]{-webkit-appearance:none;width:100%;height:4px;border-radius:2px;background:linear-gradient(to right,${C.blue} 0%,${C.blue} var(--val,0%),${C.border} var(--val,0%),${C.border} 100%);outline:none;cursor:pointer;}
        input[type=range]::-webkit-slider-thumb{-webkit-appearance:none;width:14px;height:14px;border-radius:50%;background:${C.green};cursor:pointer;}
        tr.data-row:hover td{background:#1e1c5a!important;}
        html,body,#root{width:100%;height:100%;margin:0;padding:0;}
        .app-wrap{width:100%;min-height:100vh;}
        .main-content{padding:18px 28px;width:100%;box-sizing:border-box;}
        @media(min-width:1400px){.main-content{padding:20px 48px;}}
        @media(max-width:768px){.main-content{padding:12px 14px;}}
      `}</style>

      {/* Header */}
      <div style={{background:`linear-gradient(135deg,${C.blue},#1a1660)`,padding:"14px 22px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid #3d38c4",width:"100%",boxSizing:"border-box"}}>
        <div style={{display:"flex",alignItems:"center",gap:"12px"}}>
          <div style={{background:C.green,color:C.bg,fontWeight:900,fontSize:"11px",padding:"4px 9px",borderRadius:"4px",letterSpacing:"0.07em"}}>RANGERS</div>
          <div>
            <div style={{fontWeight:800,fontSize:"14px"}}>Lead Research Pipeline</div>
            <div style={{fontSize:"10px",color:C.lavender}}>Claude Haiku · Live Search · Multi-Market {dbLoading?"· Loading...":""}</div>
          </div>
        </div>
        <div style={{display:"flex",gap:"16px",alignItems:"center"}}>
          <div style={{textAlign:"center"}}><div style={{fontSize:"18px",fontWeight:900,color:C.green,lineHeight:1}}>{pipeline.length}</div><div style={{fontSize:"9px",color:C.lavender}}>PIPELINE</div></div>
          <div style={{textAlign:"center"}}><div style={{fontSize:"18px",fontWeight:900,color:C.lavender,lineHeight:1}}>{history.length}</div><div style={{fontSize:"9px",color:C.lavender}}>HISTORY</div></div>
          <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:"3px",borderLeft:"1px solid #3d38c4",paddingLeft:"16px"}}>
            <span style={{fontSize:"10px",color:C.lavender,maxWidth:"140px",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{session?.user?.email}</span>
            <button onClick={onSignOut} style={{background:"none",border:"none",color:"#888",fontFamily:"'Mulish',sans-serif",fontSize:"10px",cursor:"pointer",padding:0}}>Sign out</button>
          </div>
        </div>
      </div>

      {/* Tier bar */}
      {pipeline.length>0 && (
        <div style={{background:C.card,borderBottom:`1px solid ${C.border}`,padding:"7px 22px",display:"flex",gap:"18px",alignItems:"center"}}>
          {Object.entries(tierCounts).map(([tier,count])=>(
            <div key={tier} style={{display:"flex",alignItems:"center",gap:"5px"}}>
              <div style={{width:"7px",height:"7px",borderRadius:"50%",background:scoreColor(tier==="Hot"?85:tier==="Warm"?65:tier==="Cool"?45:20)}}/>
              <span style={{fontSize:"11px",color:C.grey}}>{tier}</span>
              <span style={{fontSize:"12px",fontWeight:800,color:C.white}}>{count}</span>
            </div>
          ))}
          <div style={{marginLeft:"auto",fontSize:"10px",color:"#555"}}>{searchCount} search{searchCount!==1?"es":""}</div>
        </div>
      )}

      {/* Tabs */}
      <div style={{display:"flex",borderBottom:`1px solid ${C.border}`,padding:"0 22px",background:C.bg}}>
        {[{id:"search",label:"🔍 Search"},{id:"history",label:`🕐 History (${history.length})`},{id:"pipeline",label:`📊 Pipeline (${pipeline.length})`}].map(t=>(
          <button key={t.id} className="tab" onClick={()=>setActiveTab(t.id)} style={{color:activeTab===t.id?C.green:"#666",fontWeight:700,fontSize:"11px",padding:"11px 14px 9px",borderBottom:activeTab===t.id?`2px solid ${C.green}`:"2px solid transparent",letterSpacing:"0.05em",textTransform:"uppercase"}}>{t.label}</button>
        ))}
      </div>

      <div className="main-content">

        {/* ══ SEARCH TAB ══ */}
        {activeTab==="search" && (
          <div className="fade-in">
            <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:"10px",padding:"16px",marginBottom:"14px"}}>
              {/* Query */}
              <div style={{display:"flex",gap:"8px",marginBottom:"14px"}}>
                <input ref={inputRef} value={query} onChange={e=>setQuery(e.target.value)} onKeyDown={e=>e.key==="Enter"&&searchLeads()}
                  placeholder='e.g. "restaurant chains" or "event agencies"'
                  style={{flex:1,background:C.inner,border:`1px solid ${C.blue}`,borderRadius:"7px",color:C.white,fontFamily:"'Mulish',sans-serif",fontSize:"13px",padding:"10px 14px"}}/>
                <button onClick={searchLeads} disabled={loading||!query.trim()||selCountries.length===0} style={{background:loading||selCountries.length===0?C.border:C.blue,color:C.green,border:"none",borderRadius:"7px",fontFamily:"'Mulish',sans-serif",fontWeight:800,fontSize:"12px",padding:"10px 20px",cursor:loading||selCountries.length===0?"not-allowed":"pointer",whiteSpace:"nowrap"}}>
                  {loading?"Searching...":"Search →"}
                </button>
              </div>

              {/* Filters */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:"12px",marginBottom:"12px"}}>
                <div>
                  <div style={{fontSize:"10px",color:C.grey,fontWeight:700,letterSpacing:"0.06em",marginBottom:"7px"}}>COUNTRY</div>
                  <div style={{display:"flex",gap:"5px"}}>
                    {COUNTRIES.map(c=><button key={c} onClick={()=>toggleCountry(c)} style={btnStyle(selCountries.includes(c))}>{c}</button>)}
                  </div>
                </div>
                <div>
                  <div style={{fontSize:"10px",color:C.grey,fontWeight:700,letterSpacing:"0.06em",marginBottom:"7px"}}>STAFF TYPE</div>
                  <div style={{display:"flex",gap:"5px"}}>
                    {STAFF_TYPES.map(s=><button key={s} onClick={()=>setStaffType(s)} style={{...btnStyle(staffType===s),fontSize:"10px",padding:"4px 9px"}}>{s}</button>)}
                  </div>
                </div>
                <div>
                  <div style={{fontSize:"10px",color:C.grey,fontWeight:700,letterSpacing:"0.06em",marginBottom:"7px"}}>RESULTS · <span style={{color:C.green,fontWeight:900}}>{maxResults}</span></div>
                  <input type="range" min={3} max={20} step={1} value={maxResults} onChange={e=>setMaxResults(Number(e.target.value))} style={{"--val":`${((maxResults-3)/17)*100}%`}}/>
                </div>
              </div>

              {/* Verticals */}
              <div>
                <div style={{fontSize:"10px",color:C.grey,fontWeight:700,letterSpacing:"0.06em",marginBottom:"7px"}}>
                  VERTICALS
                  <button onClick={()=>setSelVerticals([...VERTICALS])} style={{marginLeft:"8px",background:"none",border:"none",color:C.lavender,fontSize:"10px",cursor:"pointer",fontFamily:"'Mulish',sans-serif"}}>All</button>
                  <button onClick={()=>setSelVerticals([])} style={{marginLeft:"4px",background:"none",border:"none",color:C.grey,fontSize:"10px",cursor:"pointer",fontFamily:"'Mulish',sans-serif"}}>None</button>
                </div>
                <div style={{display:"flex",gap:"6px",flexWrap:"wrap"}}>
                  {VERTICALS.map(v=><button key={v} onClick={()=>toggleVertical(v)} style={{...btnStyle(selVerticals.includes(v)),fontSize:"10px",padding:"4px 10px"}}>{v}</button>)}
                </div>
              </div>
            </div>

            {/* Quick prompts */}
            <div style={{display:"flex",gap:"6px",flexWrap:"wrap",marginBottom:"16px"}}>
              {QUICK.map(p=>(
                <span key={p} className="chip" onClick={()=>setQuery(p)} style={{background:C.card,border:`1px solid #2a2870`,borderRadius:"16px",color:C.grey,fontSize:"11px",padding:"4px 11px"}}>{p}</span>
              ))}
            </div>

            {loading && (
              <div style={{textAlign:"center",padding:"48px 0"}}>
                <div className="pulse" style={{fontSize:"28px",marginBottom:"10px"}}>🔍</div>
                <div style={{color:C.lavender,fontSize:"13px",fontWeight:700}}>Searching {selCountries.map(c=>COUNTRY_NAMES[c]).join(", ")}...</div>
                <div style={{color:"#444",fontSize:"11px",marginTop:"5px"}}>Live web search · Scoring · Finding contacts</div>
              </div>
            )}

            {error && <div style={{background:"#1e0f0f",border:"1px solid #7a1c1c",borderRadius:"8px",padding:"11px 15px",color:"#ff7070",fontSize:"12px",marginBottom:"14px"}}>⚠️ {error}</div>}

            {/* Results grouped by country */}
            {Object.keys(searchResults).length>0 && !loading && (
              <div className="fade-in">
                {/* Selection bar */}
                <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:"10px",padding:"12px 16px",marginBottom:"14px"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"10px"}}>
                    <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
                      <span style={{fontWeight:800,fontSize:"13px",color:C.white}}>{Object.values(searchResults).flat().length} results</span>
                      {lastBatch?.dupesRemoved>0 && <span style={{fontSize:"11px",color:"#888"}}>· {lastBatch.dupesRemoved} duplicate{lastBatch.dupesRemoved!==1?"s":""} hidden</span>}
                      <button onClick={()=>setSelectedIds(new Set(allVisibleIds))} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:"5px",color:C.lavender,fontFamily:"'Mulish',sans-serif",fontSize:"11px",padding:"3px 10px",cursor:"pointer"}}>Select All</button>
                      <button onClick={()=>setSelectedIds(new Set())} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:"5px",color:C.grey,fontFamily:"'Mulish',sans-serif",fontSize:"11px",padding:"3px 10px",cursor:"pointer"}}>None</button>
                      <span style={{fontSize:"11px",color:C.grey}}>{selectedIds.size} selected</span>
                    </div>
                    <button onClick={addSelectedToPipeline} disabled={selectedIds.size===0} style={{background:selectedIds.size===0?C.border:C.green,color:selectedIds.size===0?"#444":C.bg,border:"none",borderRadius:"6px",fontFamily:"'Mulish',sans-serif",fontWeight:800,fontSize:"12px",padding:"8px 18px",cursor:selectedIds.size===0?"not-allowed":"pointer"}}>
                      Add {selectedIds.size} to Pipeline →
                    </button>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:"12px"}}>
                    <span style={{fontSize:"10px",color:C.grey,fontWeight:700,whiteSpace:"nowrap"}}>MIN SCORE</span>
                    <input type="range" min={0} max={90} step={5} value={minScoreFilter} onChange={e=>setMinScoreFilter(Number(e.target.value))} style={{"--val":`${(minScoreFilter/90)*100}%`}}/>
                    <span style={{fontSize:"13px",fontWeight:900,color:scoreColor(minScoreFilter||1),minWidth:"36px"}}>{minScoreFilter}+</span>
                  </div>
                </div>

                {selCountries.filter(c=>searchResults[c]?.length>0).map(country=>(
                  <div key={country} style={{marginBottom:"24px"}}>
                    <div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"10px"}}>
                      <div style={{background:C.blue,color:C.green,fontWeight:900,fontSize:"11px",padding:"4px 12px",borderRadius:"6px",letterSpacing:"0.06em"}}>{COUNTRY_NAMES[country]} ({country})</div>
                      <div style={{height:"1px",flex:1,background:C.border}}/>
                      <span style={{fontSize:"11px",color:"#555"}}>{searchResults[country]?.length} leads</span>
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:"8px"}}>
                      {searchResults[country].filter(l=>l.qualifyingScore>=minScoreFilter).map(lead=>(
                        <LeadCard key={lead.id} lead={lead} mode="search" isExpanded={expandedId===lead.id} onToggle={()=>setExpandedId(expandedId===lead.id?null:lead.id)} isSelected={selectedIds.has(lead.id)} onSelect={toggleSelect} showCheckbox={true}/>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!loading&&Object.keys(searchResults).length===0&&!error && (
              <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:"10px",padding:"16px 18px"}}>
                <div style={{fontWeight:800,fontSize:"12px",color:C.lavender,marginBottom:"7px"}}>💡 How to use</div>
                <div style={{fontSize:"12px",color:C.grey,lineHeight:"1.9"}}>Select countries, staff type, and verticals. Type a business description and hit Search. Results appear grouped by country with full relevancy scores and breakdowns. Select leads individually, in bulk, or all — then add to your pipeline.</div>
              </div>
            )}
          </div>
        )}

        {/* ══ HISTORY TAB ══ */}
        {activeTab==="history" && (
          <div className="fade-in">
            <div style={{display:"flex",gap:"8px",marginBottom:"14px"}}>
              {["All","Accepted","Rejected","Pending"].map(f=>(
                <button key={f} onClick={()=>setHistoryFilter(f)} style={btnStyle(historyFilter===f)}>
                  {f} ({f==="All"?history.length:history.filter(l=>(l.historyStatus||"Pending")===f).length})
                </button>
              ))}
            </div>
            {filteredHistory.length===0 ? (
              <div style={{textAlign:"center",padding:"48px 0",color:"#444"}}>
                <div style={{fontSize:"28px",marginBottom:"10px"}}>🕐</div>
                <div style={{fontSize:"13px"}}>No history yet. Run a search and add leads.</div>
              </div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:"8px"}}>
                {filteredHistory.map(lead=>(
                  <LeadCard key={lead.id} lead={lead} mode="history" isExpanded={expandedId===lead.id} onToggle={()=>setExpandedId(expandedId===lead.id?null:lead.id)} onAccept={acceptFromHistory} onReject={rejectFromHistory} showCheckbox={false}/>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ══ PIPELINE TAB ══ */}
        {activeTab==="pipeline" && (
          <div className="fade-in">
            {lastBatch && (
              <div style={{background:"#0e1e0e",border:"1px solid #1e4d1e",borderRadius:"8px",padding:"10px 16px",marginBottom:"14px",display:"flex",gap:"16px",alignItems:"center"}}>
                <span style={{color:C.green,fontWeight:800,fontSize:"13px"}}>✓ Added to pipeline</span>
                <span><span style={{color:C.green,fontWeight:900}}>{lastBatch.added}</span> <span style={{color:C.grey,fontSize:"12px"}}>new leads</span></span>
                <button onClick={()=>setLastBatch(null)} style={{marginLeft:"auto",background:"none",border:"none",color:"#444",cursor:"pointer",fontSize:"16px"}}>×</button>
              </div>
            )}

            {pipeline.length===0 ? (
              <div style={{textAlign:"center",padding:"56px 0"}}>
                <div style={{fontSize:"32px",marginBottom:"10px"}}>📊</div>
                <div style={{fontSize:"13px",color:"#444"}}>Pipeline is empty. Search and add leads.</div>
                <button onClick={()=>setActiveTab("search")} style={{marginTop:"14px",background:C.blue,color:C.green,border:"none",borderRadius:"6px",fontFamily:"'Mulish',sans-serif",fontWeight:800,fontSize:"12px",padding:"9px 18px",cursor:"pointer"}}>Start Searching →</button>
              </div>
            ) : (
              <>
                {/* Toolbar */}
                <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:"10px",padding:"12px 16px",marginBottom:"14px"}}>
                  <div style={{display:"flex",gap:"8px",alignItems:"center",marginBottom:"10px",flexWrap:"wrap"}}>
                    <select value={pipelineSortBy} onChange={e=>setPipelineSortBy(e.target.value)} style={{background:C.inner,border:`1px solid ${C.border}`,borderRadius:"6px",color:C.lavender,fontFamily:"'Mulish',sans-serif",fontSize:"11px",padding:"6px 10px"}}>
                      <option value="qualifyingScore">Sort: Score</option>
                      <option value="name">Sort: Name A–Z</option>
                    </select>
                    <span style={{fontSize:"11px",color:C.grey}}>{pipelineLeads.length} of {pipeline.length} leads</span>
                    <div style={{marginLeft:"auto",display:"flex",gap:"8px",alignItems:"center"}}>
                      <button onClick={exportXLSX} style={{background:C.green,color:C.bg,border:"none",borderRadius:"6px",fontFamily:"'Mulish',sans-serif",fontWeight:800,fontSize:"11px",padding:"7px 14px",cursor:"pointer"}}>↓ Export CSV</button>
                      <button onClick={syncToGoogleSheet} disabled={syncing} style={{background:syncing?"#1a1850":"#1a2e1a",color:syncing?"#444":C.green,border:`1px solid ${syncing?"#1a1850":"#2e4d2e"}`,borderRadius:"6px",fontFamily:"'Mulish',sans-serif",fontWeight:800,fontSize:"11px",padding:"7px 14px",cursor:syncing?"not-allowed":"pointer",whiteSpace:"nowrap"}}>
                        {syncing?"Syncing...":"↑ Sync to Google Sheet"}
                      </button>
                    </div>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:"12px"}}>
                    <span style={{fontSize:"10px",color:C.grey,fontWeight:700,whiteSpace:"nowrap"}}>MIN SCORE</span>
                    <input type="range" min={0} max={90} step={5} value={pipelineMinScore} onChange={e=>setPipelineMinScore(Number(e.target.value))} style={{"--val":`${(pipelineMinScore/90)*100}%`}}/>
                    <span style={{fontSize:"13px",fontWeight:900,color:scoreColor(pipelineMinScore||1),minWidth:"28px"}}>{pipelineMinScore}+</span>
                  </div>
                  {syncMsg && (
                    <div style={{marginTop:"8px",padding:"8px 12px",borderRadius:"6px",fontSize:"12px",fontWeight:600,background:syncMsg.type==="success"?"#0e1e0e":"#1e0f0f",color:syncMsg.type==="success"?C.green:"#ff7070",border:`1px solid ${syncMsg.type==="success"?"#1e4d1e":"#7a1c1c"}`}}>
                      {syncMsg.text}
                      {syncMsg.type==="error" && syncMsg.text.includes("not configured") && (
                        <span style={{color:"#888",fontWeight:400}}> — Google Sheet sync will be enabled after service account setup.</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Spreadsheet table */}
                <div style={{overflowX:"auto",borderRadius:"10px",border:`1px solid ${C.border}`}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:"11px",minWidth:"1200px"}}>
                    <thead>
                      <tr style={{background:C.blue}}>
                        {["#","Company","Website","Score","Manpower Needs","Name PIC","Phone","Phone 2","Email","Instagram","TikTok","Facebook","State","Industry","Org Size","Date Created","Date First Contact","Date Last Contact","Source","Status","Department","Opp Size","Notes"].map(h=>(
                          <th key={h} style={{padding:"9px 10px",textAlign:"left",color:C.green,fontWeight:800,fontSize:"10px",letterSpacing:"0.04em",whiteSpace:"nowrap",borderRight:`1px solid #3d38c4`}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pipelineLeads.map((lead,i)=>{
                        const color=scoreColor(lead.qualifyingScore);
                        const bg=i%2===0?C.card:C.inner;
                        const inp = {background:bg,border:"none",color:C.white,fontFamily:"'Mulish',sans-serif",fontSize:"11px",width:"100%",padding:"3px 5px",borderRadius:"4px"};
                        const sel = {background:bg,border:`1px solid ${C.border}`,color:C.white,fontFamily:"'Mulish',sans-serif",fontSize:"10px",width:"100%",padding:"3px 5px",borderRadius:"4px",cursor:"pointer"};
                        const td = (w) => ({padding:"3px 5px",borderRight:`1px solid ${C.border}`,background:bg,minWidth:w||"90px"});
                        return (
                          <tr key={lead.id} className="data-row" style={{borderBottom:`1px solid ${C.border}`}}>
                            <td style={{...td("30px"),textAlign:"center",color:"#555",fontSize:"10px"}}>{i+1}</td>
                            <td style={td("140px")}><input value={lead.company||""} onChange={e=>updateLead(lead.id,"company",e.target.value)} style={{...inp,fontWeight:700}}/></td>
                            <td style={td("140px")}><input value={lead.website||""} onChange={e=>updateLead(lead.id,"website",e.target.value)} placeholder="—" style={{...inp,color:C.lavender}}/></td>
                            <td style={{...td("60px"),textAlign:"center"}}>
                              <span style={{background:color+"22",color,border:`1px solid ${color}44`,padding:"2px 7px",borderRadius:"4px",fontSize:"10px",fontWeight:800}}>{lead.qualifyingScore}</span>
                            </td>
                            <td style={td("160px")}><input value={lead.temporaryManpowerNeeds||""} onChange={e=>updateLead(lead.id,"temporaryManpowerNeeds",e.target.value)} placeholder="—" style={{...inp,color:C.grey}}/></td>
                            <td style={td("110px")}><input value={lead.name||""} onChange={e=>updateLead(lead.id,"name",e.target.value)} placeholder="—" style={inp}/></td>
                            <td style={td("120px")}><input value={lead.phone||""} onChange={e=>updateLead(lead.id,"phone",e.target.value)} placeholder="—" style={inp}/></td>
                            <td style={td("120px")}><input value={lead.phone2||""} onChange={e=>updateLead(lead.id,"phone2",e.target.value)} placeholder="—" style={inp}/></td>
                            <td style={td("160px")}><input value={lead.email||""} onChange={e=>updateLead(lead.id,"email",e.target.value)} placeholder="—" style={{...inp,color:C.green}}/></td>
                            <td style={td("110px")}><input value={lead.instagram||""} onChange={e=>updateLead(lead.id,"instagram",e.target.value)} placeholder="—" style={inp}/></td>
                            <td style={td("110px")}><input value={lead.tiktok||""} onChange={e=>updateLead(lead.id,"tiktok",e.target.value)} placeholder="—" style={inp}/></td>
                            <td style={td("110px")}><input value={lead.facebook||""} onChange={e=>updateLead(lead.id,"facebook",e.target.value)} placeholder="—" style={inp}/></td>
                            <td style={td("120px")}>
                              <select value={lead.state||""} onChange={e=>updateLead(lead.id,"state",e.target.value)} style={sel}>
                                <option value="">—</option>
                                {STATE_OPTS.map(o=><option key={o} value={o}>{o}</option>)}
                              </select>
                            </td>
                            <td style={td("150px")}>
                              <select value={lead.industry||""} onChange={e=>updateLead(lead.id,"industry",e.target.value)} style={sel}>
                                <option value="">—</option>
                                {INDUSTRY_OPTS.map(o=><option key={o} value={o}>{o}</option>)}
                              </select>
                            </td>
                            <td style={td("80px")}>
                              <select value={lead.orgSize||""} onChange={e=>updateLead(lead.id,"orgSize",e.target.value)} style={sel}>
                                <option value="">—</option>
                                {ORG_SIZE_OPTS.map(o=><option key={o} value={o}>{o}</option>)}
                              </select>
                            </td>
                            <td style={td("110px")}><input type="date" value={lead.dateCreated||""} onChange={e=>updateLead(lead.id,"dateCreated",e.target.value)} style={{...inp,colorScheme:"dark"}}/></td>
                            <td style={td("110px")}><input type="date" value={lead.dateFirstContact||""} onChange={e=>updateLead(lead.id,"dateFirstContact",e.target.value)} style={{...inp,colorScheme:"dark"}}/></td>
                            <td style={td("110px")}><input type="date" value={lead.dateLastContact||""} onChange={e=>updateLead(lead.id,"dateLastContact",e.target.value)} style={{...inp,colorScheme:"dark"}}/></td>
                            <td style={td("150px")}>
                              <select value={lead.source||"Rangers Research Tool"} onChange={e=>updateLead(lead.id,"source",e.target.value)} style={sel}>
                                {SOURCE_OPTS.map(o=><option key={o} value={o}>{o}</option>)}
                              </select>
                            </td>
                            <td style={td("150px")}>
                              <select value={lead.pipelineStatus||"New"} onChange={e=>updateLead(lead.id,"pipelineStatus",e.target.value)} style={{...sel,color:C.lavender}}>
                                {STATUS_OPTS.map(o=><option key={o} value={o}>{o}</option>)}
                              </select>
                            </td>
                            <td style={td("110px")}>
                              <select value={lead.department||""} onChange={e=>updateLead(lead.id,"department",e.target.value)} style={sel}>
                                <option value="">—</option>
                                {DEPT_OPTS.map(o=><option key={o} value={o}>{o}</option>)}
                              </select>
                            </td>
                            <td style={td("120px")}>
                              <select value={lead.oppSize||""} onChange={e=>updateLead(lead.id,"oppSize",e.target.value)} style={sel}>
                                <option value="">—</option>
                                {OPP_SIZE_OPTS.map(o=><option key={o} value={o}>{o}</option>)}
                              </select>
                            </td>
                            <td style={{...td("160px"),borderRight:"none"}}><input value={lead.notes||""} onChange={e=>updateLead(lead.id,"notes",e.target.value)} placeholder="Notes..." style={{...inp,color:C.grey}}/></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
